import { createTheme } from '@mui/material/styles';
import '@mui/material/Typography';
import {
  brandColors,
  neutralColors,
  semanticColors,
  systemColors,
} from './colors';

// Module augmentation for custom typography variant
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display1: true;
    body1Normal: true;
    body1Medium: true;
    body1SemiBold: true;
    body1Bold: true;
    body2Medium: true;
    body2SemiBold: true;
    body2Bold: true;
    caption1Medium: true;
    caption1SemiBold: true;
    caption1Bold: true;
    caption1Normal: true;
    subtitle3: true;
  }
}

// Module augmentation for custom AppBar variant
declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    default: true;
    message: true;
  }
}

// Module augmentation for custom Alert variant
declare module '@mui/material/Alert' {
  interface AlertPropsColorOverrides {
    secondary: true;
  }
}

// Augment the palette to include mcGovern colors
declare module '@mui/material/styles' {
  interface Palette {
    mcGovernTeal: Palette['primary'];
    mcGovernGold: Palette['primary'];
    mcGovernRust: Palette['secondary'];
    mcGovernLime: Palette['secondary'];
    mcGovernPeacock: Palette['secondary'];
    mcGovernDarkTeal: Palette['secondary'];
    mcGovernMediumTeal: Palette['secondary'];
    mcGovernCaramel: Palette['secondary'];
    mcGovernPine: Palette['secondary'];
    mcGovernMediumGray: Palette['secondary'];
    mcGovernLightTeal: Palette['background'];
    mcGovernLightLime: Palette['background'];
    mcGovernLightGold: Palette['background'];
    mcGovernLightPine: Palette['background'];
    mcGovernLightGray: Palette['background'];
  }

  interface PaletteOptions {
    mcGovernTeal?: PaletteOptions['primary'];
    mcGovernGold?: PaletteOptions['primary'];
    mcGovernRust?: PaletteOptions['secondary'];
    mcGovernLime?: PaletteOptions['secondary'];
    mcGovernPeacock?: PaletteOptions['secondary'];
    mcGovernDarkTeal?: PaletteOptions['secondary'];
    mcGovernMediumTeal?: PaletteOptions['secondary'];
    mcGovernCaramel?: PaletteOptions['secondary'];
    mcGovernPine?: PaletteOptions['secondary'];
    mcGovernMediumGray?: PaletteOptions['secondary'];
    mcGovernLightTeal?: PaletteOptions['background'];
    mcGovernLightLime?: PaletteOptions['background'];
    mcGovernLightGold?: PaletteOptions['background'];
    mcGovernLightPine?: PaletteOptions['background'];
    mcGovernLightGray?: PaletteOptions['background'];
  }
}

// Primary colors
const primaryColors = {
  mcGovernTeal: {
    main: brandColors.mcGovernTeal,
  },
  mcGovernGold: {
    main: brandColors.mcGovernGold,
  },
};

// Secondary colors
const secondaryColors = {
  mcGovernRust: {
    main: brandColors.mcGovernRust,
    contrastText: neutralColors.white,
  },
  mcGovernLime: {
    main: brandColors.mcGovernLime,
  },
  mcGovernPeacock: {
    main: brandColors.mcGovernPeacock,
  },
  mcGovernDarkTeal: {
    main: brandColors.mcGovernDarkTeal,
  },
  mcGovernMediumTeal: {
    main: brandColors.mcGovernMediumTeal,
  },
  mcGovernCaramel: {
    main: brandColors.mcGovernCaramel,
  },
  mcGovernPine: {
    main: brandColors.mcGovernPine,
  },
  mcGovernMediumGray: {
    main: brandColors.mcGovernMediumGray,
  },
};

// Tint colors
const tintColors = {
  mcGovernLightTeal: {
    default: brandColors.mcGovernLightTeal,
  },
  mcGovernLightLime: {
    default: brandColors.mcGovernLightLime,
  },
  mcGovernLightGold: {
    default: brandColors.mcGovernLightGold,
  },
  mcGovernLightPine: {
    default: brandColors.mcGovernLightPine,
  },
  mcGovernLightGray: {
    default: brandColors.mcGovernLightGray,
  },
};

// Default colors
const defaultColors = {
  background: {
    default: semanticColors.backgroundPrimary, // Depended on <CssBaseline />
  },
  primary: {
    main: brandColors.mcGovernTeal,
  },
  secondary: {
    main: neutralColors.black,
  },
};

const McGovernTheme = createTheme({
  palette: {
    ...defaultColors,
    ...primaryColors,
    ...secondaryColors,
    ...tintColors,
    success: {
      main: systemColors.successGreen,
      light: systemColors.successGreenLight,
    },
    error: {
      main: systemColors.errorRed,
      light: systemColors.errorRedLight,
    },
    warning: {
      main: systemColors.warningYellow,
      light: systemColors.warningYellowLight,
    },
    info: {
      main: systemColors.infoBlue,
      light: systemColors.infoBlueLight,
    },
  },
  typography: {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: 'display1' is not recognized by TypeScript yet
    display1: {
      fontSize: '6.5rem', // 56px
      lineHeight: '120%',
      fontWeight: '700',
    },
    h1: {
      fontSize: '3.5rem', // 56px
      lineHeight: '120%',
      fontWeight: '700',
    },
    h2: {
      fontSize: '3rem', // 48px
      lineHeight: '120%',
      fontWeight: '700',
    },
    h3: {
      fontSize: '2.5', // 40px
      lineHeight: '120%',
      fontWeight: '700',
    },
    h4: {
      fontSize: '2rem', // 32px
      lineHeight: '130%',
      fontWeight: '700',
    },
    h5: {
      fontSize: '1.5rem', // 24px
      lineHeight: '140%',
      fontWeight: '700',
    },
    h6: {
      fontSize: '1.25rem', // 20px
      lineHeight: '140%',
      fontWeight: '700',
    },
    subtitle1: {
      fontSize: '1.125rem', // 18px
      lineHeight: '150%',
      fontWeight: '600',
    },
    subtitle2: {
      fontSize: '1.125rem', // 18px
      lineHeight: '150%',
      fontWeight: '500',
    },
    subtitle3: {
      fontSize: '1.125rem', // 18px
      lineHeight: '150%',
      fontWeight: '400',
    },
    body1: {
      fontSize: '1rem', // 16px
      lineHeight: '150%',
      fontWeight: '400',
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: 'body1Medium' is not recognized by TypeScript yet
    body1Normal: {
      fontSize: '0.75rem', // 16px
      lineHeight: '1.125rem',
      fontWeight: '400',
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: 'body1Medium' is not recognized by TypeScript yet
    body1Medium: {
      fontSize: '1rem', // 16px
      lineHeight: '150%',
      fontWeight: '600',
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: 'body1SemiBold' is not recognized by TypeScript yet
    body1SemiBold: {
      fontSize: '1rem', // 16px
      lineHeight: '150%',
      fontWeight: '700',
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: 'body1Bold' is not recognized by TypeScript yet
    body1Bold: {
      fontSize: '1rem', // 16px
      lineHeight: '150%',
      fontWeight: '800',
    },
    body2: {
      fontSize: '0.875rem', // 14px
      lineHeight: '150%',
      fontWeight: '400',
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: 'body1Medium' is not recognized by TypeScript yet
    body2Medium: {
      fontSize: '0.875rem', // 14px
      lineHeight: '150%',
      fontWeight: '600',
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: 'body1SemiBold' is not recognized by TypeScript yet
    body2SemiBold: {
      fontSize: '0.875rem', // 14px
      lineHeight: '150%',
      fontWeight: '700',
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: 'body1Bold' is not recognized by TypeScript yet
    body2Bold: {
      fontSize: '0.875rem', // 14px
      lineHeight: '150%',
      fontWeight: '800',
    },
    caption: {
      fontSize: '0.75rem', // 12px
      lineHeight: '150%',
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: 'body1Medium' is not recognized by TypeScript yet
    caption1Normal: {
      fontSize: '0.75rem', // 12px
      lineHeight: '150%',
      fontWeight: '400',
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: 'body1Medium' is not recognized by TypeScript yet
    caption1Medium: {
      fontSize: '0.75rem', // 12px
      lineHeight: '150%',
      fontWeight: '600',
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: 'body1SemiBold' is not recognized by TypeScript yet
    caption1SemiBold: {
      fontSize: '0.75rem', // 12px
      lineHeight: '150%',
      fontWeight: '700',
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: 'body1Bold' is not recognized by TypeScript yet
    caption1Bold: {
      fontSize: '0.75rem', // 12px
      lineHeight: '150%',
      fontWeight: '800',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: '0',
          borderTop: '0',
          borderLeft: '0',
          borderRight: '0',
        },
      },
      variants: [
        {
          props: { variant: 'default' },
          style: {
            backgroundImage: `linear-gradient(to right, ${brandColors.mcGovernLightGold}, ${brandColors.mcGovernLightLime})`,
            borderBottomStyle: 'solid',
            borderBottomWidth: '0.125rem',
            borderImage: `linear-gradient(to right,
              ${brandColors.mcGovernGold} 21%,
              ${brandColors.mcGovernRust} 60%,
              ${brandColors.mcGovernTeal} 100%)`,
            borderImageSlice: '1',
          },
        },
        {
          props: { variant: 'message' },
          style: {
            backgroundColor: '#000', // Replace with your desired color
          },
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderColor: `${semanticColors.borderSecondary}`,
          borderStyle: 'solid',
          borderWidth: '0.125rem',
          borderRadius: '.5rem',
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderColor: `${semanticColors.borderSecondary}`,
          borderStyle: 'solid',
          borderWidth: '0.125rem', // 2px
          borderRadius: '1rem', // 16px
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          borderRadius: '0',
        },
        paper: {
          padding: '0',
          borderRadius: '0',
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          '& span': {
            display: 'inline-block',
            '&:first-letter': {
              textTransform: 'uppercase',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: '600',
          borderRadius: '1.5rem', // 24px
          textTransform: 'none',
        },
      },
      variants: [
        {
          props: { variant: 'contained', size: 'medium' },
          style: {
            height: '40.5px',
          },
        },
        {
          props: { variant: 'outlined' },
          style: {
            borderColor: 'inherit',
            borderStyle: 'solid',
            borderWidth: '0.188rem', // 3px
            '&:hover': {
              backgroundColor: 'none',
              color: 'none',
              borderColor: 'inherit',
              borderStyle: 'solid',
              borderWidth: '0.188rem', // 3px
            },
          },
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.MuiIconButton-colorPrimary': {
            color: neutralColors.white,
            backgroundColor: brandColors.mcGovernTeal,
            transition:
              'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            boxShadow:
              '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
            '&:hover': {
              boxShadow:
                '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
              backgroundColor: brandColors.mcGovernTeal,
            },
            '&:active': {
              boxShadow:
                '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
            },
          },
          '&.MuiIconButton-colorSecondary': {
            color: neutralColors.white,
            backgroundColor: neutralColors.black,
            transition:
              'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            boxShadow:
              '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
            '&:hover': {
              boxShadow:
                '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
              backgroundColor: neutralColors.black,
            },
            '&:active': {
              boxShadow:
                '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
            },
          },
        },
        sizeMedium: {
          '& .MuiSvgIcon-root': {
            fontSize: '1.2rem',
            margin: '.05rem',
          },
        },
        sizeLarge: {
          '& .MuiSvgIcon-root': {
            fontSize: '1.5rem',
            margin: '.14rem',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: '3rem',
          '&.Mui-error .MuiInputBase-input': {
            borderColor: systemColors.errorRed,
          },
          '&.Mui-error:hover .MuiInputBase-input': {
            borderColor: systemColors.errorRed,
          },
          '&.Mui-error.Mui-focused .MuiInputBase-input': {
            borderColor: systemColors.errorRed,
          },
        },
        input: {
          padding: '1.03125rem 1.25rem', // 16.5px 20px
          '&.analysis-verify__input': { padding: '0' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            padding: '1.03125rem 1.25rem', // 16.5px 20px
          },
          '& .MuiOutlinedInput-input': {
            padding: '1.03125rem 1.25rem', // 16.5px 20px
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: systemColors.errorRed,
          },
          '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: systemColors.errorRed,
          },
          '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: systemColors.errorRed,
          },
          '&.Mui-error .MuiSelect-icon': {
            color: systemColors.errorRed,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: neutralColors.white,
          fontWeight: '600',
          borderRadius: '1.5rem', // 24px
          textTransform: 'none',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: neutralColors.black,
              borderStyle: 'solid',
              borderWidth: '0.188rem', // 3px
              borderRadius: '1.5rem', // 24px
              top: '0',
              '& legend': {
                display: 'none', // Hide legend
              },
            },
            '&:hover fieldset': {
              borderColor: neutralColors.black,
              borderStyle: 'solid',
              borderWidth: '0.188rem', // 3px
              borderRadius: '1.5rem', // 24px
              top: '0',
              '& legend': {
                display: 'none', // Hide legend on hover
              },
            },
            '&.Mui-focused fieldset': {
              borderColor: neutralColors.black,
              borderStyle: 'solid',
              borderWidth: '0.188rem', // 3px
              borderRadius: '1.5rem', // 24px
              top: '0',
              '& legend': {
                display: 'none', // Hide legend on focus
              },
            },
            '&.Mui-disabled fieldset': {
              borderColor: 'inherit',
              borderStyle: 'solid',
              borderWidth: '0.188rem', // 3px
              borderRadius: '1.5rem', // 24px
              top: '0',
            },
          },
        },
      },
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: 'display1' is not recognized by TypeScript yet
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          backgroundColor: neutralColors.white,
          fontWeight: '600',
          borderRadius: '1.5rem', // 24px
          textTransform: 'none',
          '& .MuiPickersInputBase-root': {
            height: '3rem',
            '& .MuiPickersSectionList-sectionContent': {
              color: neutralColors.black,
            },
          },
          '& .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: neutralColors.black,
            borderStyle: 'solid',
            borderWidth: '0.188rem', // 3px
            borderRadius: '1.5rem', // 24px
            top: '0',
            '& legend': {
              display: 'none', // Hide legend
            },
          },
          '&:hover .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: neutralColors.black,
            borderStyle: 'solid',
            borderWidth: '0.188rem', // 3px
            borderRadius: '1.5rem', // 24px
            top: '0',
            '& legend': {
              display: 'none', // Hide legend on hover
            },
          },
          '& .MuiPickersInputBase-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline':
            {
              borderColor: neutralColors.black,
              borderStyle: 'solid',
              borderWidth: '0.188rem', // 3px
              borderRadius: '1.5rem', // 24px
              top: '0',
              '& legend': {
                display: 'none', // Hide legend on focus
              },
            },
          '& .MuiPickersInputBase-root.Mui-disabled .MuiPickersOutlinedInput-notchedOutline':
            {
              borderColor: 'inherit',
              borderStyle: 'solid',
              borderWidth: '0.188rem', // 3px
              borderRadius: '1.5rem', // 24px
              top: '0',
            },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: neutralColors.white,
          borderRadius: '1.5rem', // 24px
          textTransform: 'none',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: neutralColors.black,
            borderStyle: 'solid',
            borderWidth: '0.188rem', // 3px
            top: '0',
            '& legend': {
              display: 'none', // Hide legend
            },
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: neutralColors.black,
            borderStyle: 'solid',
            borderWidth: '0.188rem', // 3px
            top: '0',
            '& legend': {
              display: 'none', // Hide legend on hover
            },
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: neutralColors.black,
            borderStyle: 'solid',
            borderWidth: '0.188rem', // 3px
            top: '0',
            '& legend': {
              display: 'none', // Hide legend on focus
            },
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: systemColors.errorRed,
          },
          '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: systemColors.errorRed,
          },
          '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: systemColors.errorRed,
          },
          '&.Mui-error .MuiSelect-icon': {
            color: systemColors.errorRed,
          },
        },
        icon: {
          color: neutralColors.black,
          right: '1rem',
          position: 'absolute',
        },
        select: {
          padding: '1.03125rem 1.25rem', // 16.5px 20px
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          color: neutralColors.black,
          position: 'unset',
          left: 'unset',
          top: 'unset',
          transform: 'unset',
          '&.Mui-error': {
            color: systemColors.errorRed,
          },
        },
        asterisk: {
          color: systemColors.errorRed,
          '&.Mui-focused': {
            color: systemColors.primaryBlue,
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: neutralColors.black,
          '&:hover': {
            color: neutralColors.black,
          },
        },
        icon: {
          fill: neutralColors.black,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: `1px solid ${neutralColors.black}`,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem',
          paddingBottom: '1rem',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: 'none',
          borderRadius: '1.5rem',
          '&:before': {
            display: 'none',
          },
          '&:first-of-type': {
            borderTopLeftRadius: '1.5rem',
            borderTopRightRadius: '1.5rem',
          },
          '&:last-of-type': {
            borderBottomLeftRadius: '1.5rem',
            borderBottomRightRadius: '1.5rem',
          },
          '&.Mui-expanded': {
            backgroundColor: neutralColors.tailwind100,
          },
          '&:hover': {
            backgroundColor: neutralColors.tailwind100,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: 'inherit',
          borderRadius: '1.5rem',
          '&:hover': {
            backgroundColor: neutralColors.tailwind100,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          borderRadius: '1.5rem',
          backgroundColor: neutralColors.tailwind100,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          backgroundColor: neutralColors.white,
          borderRadius: '1.5rem', // your existing code
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: neutralColors.black,
              borderStyle: 'solid',
              borderWidth: '0.188rem',
              borderRadius: '1.5rem',
              top: '0',
              '& legend': {
                display: 'none',
              },
            },
            '&:hover fieldset': {
              borderColor: neutralColors.black,
              borderStyle: 'solid',
              borderWidth: '0.188rem',
              borderRadius: '1.5rem',
              top: '0',
              '& legend': {
                display: 'none',
              },
            },
            '&.Mui-focused fieldset': {
              borderColor: neutralColors.black,
              borderStyle: 'solid',
              borderWidth: '0.188rem',
              borderRadius: '1.5rem',
              top: '0',
              '& legend': {
                display: 'none',
              },
            },
            '&.MuiInputBase-root': {
              height: 'auto !important',
              minHeight: '3rem !important',
              alignItems: 'flex-start !important',
              flexDirection: 'column',
              flexWrap: 'wrap !important',
              '& input': {
                width: '100%',
              },
            },
          },
        },

        inputRoot: {
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          '& .MuiInputBase-input': {
            padding: '0 .5rem',
          },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.severity === 'error' && {
            borderColor: '#ffd4d4',
          }),
          ...(ownerState.severity === 'warning' && {
            borderColor: '#fffbd4',
          }),
          ...(ownerState.severity === 'info' && {
            borderColor: '#d2f2ff',
          }),
          ...(ownerState.severity === 'secondary' && {
            borderColor: neutralColors.black,
            backgroundColor: neutralColors.black,
            color: neutralColors.white,
          }),
        }),
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          lineHeight: 1,
          '& > span': {
            lineHeight: 0,
          },
        },
      },
    },
  },
});

export default McGovernTheme;
